import { newDate } from '@/utils/date';
// https://github.com/jquense/yup
import { normalizePhone } from '@/utils/sanitize';
import * as _yup from 'yup';
import type { AnyObjectSchema, StringSchema } from 'yup';

_yup.setLocale({
  mixed: {
    default: ({ label }) => `${label ? `${label}には` : ''}正しい値を入力してください`,
    required: ({ label }) => `${label ? `${label}は` : ''}必須項目です`,
    oneOf: ({ label, values }) => `${label ? `${label}には` : ''}${values}のいずれかを入力してください`,
    notOneOf: ({ label, values }) => `${label ? `${label}には` : ''}${values}以外のものを入力してください`,
    // From user's perspective, defined and default is essentially same.
    defined: ({ label }) => `${label ? `${label}には` : ''}値を入力してください`,
  },
  string: {
    length: ({ label, length }) => `${label ? `${label}は` : ''}${length}文字で入力してください`,
    min: ({ label, min }) => `${label ? `${label}は` : ''}${min}文字以上入力してください`,
    max: ({ label, max }) => `${label ? `${label}は` : ''}${max}文字以下にしてください`,
    matches: ({ label, regex }) => `${label ? `${label}は` : ''}以下の形式で入力してください: ${regex}`,
    email: ({ label }) => `${label ? `${label}には` : ''}正しいメールアドレスを入力してください`,
    url: ({ label }) => `${label ? `${label}には` : ''}正しいURLを入力してください`,
    uuid: ({ label }) => `${label ? `${label}には` : ''}正しいUUIDを入力してください`,
    trim: ({ label }) => `${label ? `${label}の` : ''}前後の空白を取り除いてください`,
    lowercase: ({ label }) => `${label ? `${label}には` : ''}小文字のみ入力してください`,
    uppercase: ({ label }) => `${label ? `${label}には` : ''}大文字のみ入力してください`,
  },
  number: {
    min: ({ label, min }) => `${label ? `${label}は` : ''}${min}以上にしてください`,
    max: ({ label, max }) => `${label ? `${label}は` : ''}${max}以下にしてください`,
    lessThan: ({ label, less }) => `${label ? `${label}は` : ''}${less}未満にしてください`,
    moreThan: ({ label, more }) => `${label ? `${label}は` : ''}${more}より大きい数にしてください`,
    positive: ({ label }) => `${label ? `${label}は` : ''}正の数にしてください`,
    negative: ({ label }) => `${label ? `${label}は` : ''}負の数にしてください`,
    integer: ({ label }) => `${label ? `${label}には` : ''}整数を入力してください`,
  },
  date: {
    min: ({ label, min }) => `${label ? `${label}には` : ''}${min}以降の日付を入力してください`,
    max: ({ label, max }) => `${label ? `${label}には` : ''}${max}以前の日付を入力してください`,
  },
  array: {
    min: ({ label }) => `${label ? `${label}を` : ''}選択してください`,
    max: ({ label, max }) => `${label ? `${label}は` : ''}${max}つ以下にしてください`,
  },
});

/**
 * 日付のカスタムバリデーション
 * eg. yup.date().dateStringValidation()
 */
_yup.addMethod<StringSchema>(_yup.string, 'dateStringValidation', function (errorMessage) {
  return this.test('dateStringValidation', errorMessage, function (value) {
    const { path, createError } = this;
    if (!value) return true;
    return Number.isNaN(newDate(value).getDate()) ? createError({ path, message: errorMessage }) : true;
  });
});

/**
 * カタカナのカスタムバリデーション
 * eg. yup.string().katakana()
 */
_yup.addMethod<StringSchema>(_yup.string, 'katakana', function (errorMessage?: string) {
  const message = errorMessage ?? '全角カタカナで入力してください';
  return this.test('katakana', message, (value) => {
    if (!value) return true;
    return /^[ァ-ヶー]+$/.test(value);
  });
});

/**
 * 電話番号のカスタムバリデーション
 * 数字のみ / ハイフン区切り / +81付きを許容。正規化後は0始まり10〜11桁。
 */
_yup.addMethod<StringSchema>(_yup.string, 'phone', function () {
  return this.required('電話番号を入力してください')
    .matches(/^[+(]?\d[\d\-() ]*[\d)]$/, '電話番号の形式が正しくありません')
    .test(
      'no-consecutive-symbols',
      '電話番号の形式が正しくありません',
      (val) => !/(-{2,}| {2,}|\({2,}|\){2,})/.test(val ?? ''),
    )
    .test('phone-digits', '番号の桁数が正しくありません', (val) => {
      const digits = normalizePhone(val ?? '');
      return /^0\d{9,10}$/.test(digits);
    });
});

/**
 * 郵便番号のカスタムバリデーション
 * 7桁連続 or ハイフン1つ区切り（3-4）のみ許容
 */
_yup.addMethod<StringSchema>(_yup.string, 'zip', function () {
  return this.required('郵便番号を入力してください').matches(
    /^\d{7}$|^\d{3}-\d{4}$/,
    '郵便番号の形式が正しくありません',
  );
});

declare module 'yup' {
  interface StringSchema {
    dateStringValidation(this: StringSchema): StringSchema;
    katakana(this: StringSchema, errorMessage?: string): StringSchema;
    phone(this: StringSchema): StringSchema;
    zip(this: StringSchema): StringSchema;
  }
}

/**
 * 同期的にバリデーションを行い、エラーメッセージを取得する
 * - react-hook-form の formState.errors ではエラーメッセージの即時取得ができないため、こちらで代用
 * @param schema
 * @param values
 * @returns
 */
const getErrorsSync = (schema: AnyObjectSchema, values: unknown): Record<string, { message: string }> => {
  try {
    schema.validateSync(values, { abortEarly: false });
    return {}; // エラーなし
  } catch (err) {
    if (err instanceof _yup.ValidationError) {
      return err.inner.reduce(
        (acc: Record<string, { message: string }>, curr: _yup.ValidationError) => {
          if (curr.path) {
            acc[curr.path] = { message: curr.message };
          }
          return acc;
        },
        {} as Record<string, { message: string }>,
      );
    }
    return {};
  }
};

export const yup = { ..._yup, getErrorsSync };
